import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

interface AttachmentFile {
  file_name: string;
  key: string;
  cdn_url: string;
  dev_url: string;
  mime_type: string;
  size: number;
  type: 'image' | 'pdf' | 'video' | 'audio' | 'doc' | 'other';
}

interface NoticeDetails {
  _id: string;
  id: string;          // ✅ Added id field
  title: string;
  body: string;
  priority: string;
  date_time: string;
  posted_by_name: string;
  files: AttachmentFile[];
  broadcast_user_type: string;
  voice_note?: AttachmentFile | null;  // ✅ Added voice_note support
  created_at?: string;
  updated_at?: string;
}

interface ViewNoticeModalProps {
  open: boolean;
  onClose: () => void;
  noticeId: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileIcon = (type: AttachmentFile['type'], mimeType: string): { icon: string; color: string; bg: string } => {
  switch (type) {
    case 'image': return { icon: 'pi pi-image',    color: '#3b82f6', bg: 'rgba(59,130,246,0.08)'  };
    case 'pdf':   return { icon: 'pi pi-file-pdf', color: '#ef4444', bg: 'rgba(239,68,68,0.08)'   };
    case 'video': return { icon: 'pi pi-video',    color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)'  };
    case 'audio': return { icon: 'pi pi-volume-up',color: '#f59e0b', bg: 'rgba(245,158,11,0.08)'  };
    case 'doc':   return { icon: 'pi pi-file',     color: '#10b981', bg: 'rgba(16,185,129,0.08)'  };
    default:      return { icon: 'pi pi-file',     color: '#6b7280', bg: 'rgba(107,114,128,0.08)' };
  }
};

const getPriorityStyle = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'high':   return { bg: '#ef4444', text: 'High Priority'   };
    case 'medium': return { bg: '#f59e0b', text: 'Medium Priority' };
    case 'low':    return { bg: '#3b82f6', text: 'Low Priority'    };
    default:       return { bg: '#10b981', text: priority || '—'   };
  }
};

const formatDateTime = (dateString: string) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return '—';
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '14px', paddingBottom: '12px',
    borderBottom: '1px solid rgba(229,231,235,0.6)'
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '32px', height: '32px', borderRadius: '8px',
      background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)', color: 'white'
    }}>
      <i className={icon} style={{ fontSize: '15px' }} />
    </div>
    <span style={{ color: '#1e40af', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
      {title}
    </span>
  </div>
);

const InfoRow: React.FC<{
  icon: string; label: string; value: string | number;
  isChip?: boolean; chipColor?: string;
}> = ({ icon, label, value, isChip = false, chipColor = '#4169E1' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 0' }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '36px', height: '36px', borderRadius: '10px',
      background: 'rgba(65,105,225,0.08)', color: '#4169E1', flexShrink: 0
    }}>
      <i className={icon} style={{ fontSize: '15px' }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>
        {label}
      </div>
      {isChip ? (
        <span style={{
          display: 'inline-block', padding: '5px 12px', borderRadius: '8px',
          background: chipColor, color: 'white', fontSize: '13px', fontWeight: 600,
          boxShadow: `0 2px 8px ${chipColor}40`
        }}>
          {value}
        </span>
      ) : (
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', lineHeight: 1.3, wordBreak: 'break-word' }}>
          {value || '—'}
        </div>
      )}
    </div>
  </div>
);

// ─── Voice Note Player ───────────────────────────────────────────────────────────

const VoiceNotePlayer: React.FC<{ file: AttachmentFile }> = ({ file }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio(file.cdn_url);
    audioRef.current.addEventListener('play', () => setIsPlaying(true));
    audioRef.current.addEventListener('pause', () => setIsPlaying(false));
    audioRef.current.addEventListener('ended', () => setIsPlaying(false));
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('play', () => setIsPlaying(true));
        audioRef.current.removeEventListener('pause', () => setIsPlaying(false));
        audioRef.current.removeEventListener('ended', () => setIsPlaying(false));
      }
    };
  }, [file.cdn_url]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '14px',
        padding: '12px 16px', borderRadius: '12px',
        background: 'rgba(139,92,246,0.06)',
        border: '1px solid rgba(139,92,246,0.15)'
      }}
    >
      <button
        onClick={togglePlay}
        style={{
          width: '44px', height: '44px', borderRadius: '50%',
          border: 'none', background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
          color: 'white', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
          transition: 'transform 0.2s ease'
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        <i className={isPlaying ? 'pi pi-pause' : 'pi pi-play'} style={{ fontSize: '18px', marginLeft: isPlaying ? 0 : '2px' }} />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.file_name || 'Voice Note'}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
          {file.size ? formatFileSize(file.size) : ''}
        </div>
      </div>
      {file.size && (
        <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 600 }}>
          {formatDuration(file.size / 1000)}
        </span>
      )}
    </div>
  );
};

// ─── Image Lightbox ───────────────────────────────────────────────────────────

const ImageLightbox: React.FC<{ src: string; alt: string; onClose: () => void }> = ({ src, alt, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)', animation: 'maskFadeIn 0.2s ease'
    }}
  >
    <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
      <img src={src} alt={alt} style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '12px', objectFit: 'contain', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' }} />
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: '-12px', right: '-12px',
          width: '32px', height: '32px', borderRadius: '50%',
          background: 'white', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
      >
        <i className="pi pi-times" style={{ fontSize: '13px', color: '#374151' }} />
      </button>
    </div>
  </div>
);

// ─── Attachment Card ──────────────────────────────────────────────────────────

const AttachmentCard: React.FC<{ file: AttachmentFile; onImageClick: (url: string) => void }> = ({ file, onImageClick }) => {
  const { icon, color, bg } = getFileIcon(file.type, file.mime_type);
  const isImage = file.type === 'image';

  return (
    <div style={{
      borderRadius: '12px', overflow: 'hidden',
      border: '1px solid #e5e7eb', background: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      transition: 'all 0.25s ease', cursor: 'pointer'
    }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 12px 24px ${color}25`;
        e.currentTarget.style.borderColor = color;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
        e.currentTarget.style.borderColor = '#e5e7eb';
      }}
      onClick={() => isImage ? onImageClick(file.cdn_url) : window.open(file.cdn_url, '_blank')}
    >
      {/* Preview area */}
      {isImage ? (
        <div style={{ position: 'relative', height: '130px', overflow: 'hidden', background: '#f8fafc' }}>
          <img
            src={file.cdn_url}
            alt={file.file_name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={e => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.style.display = 'flex';
              (e.target as HTMLImageElement).parentElement!.style.alignItems = 'center';
              (e.target as HTMLImageElement).parentElement!.style.justifyContent = 'center';
            }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(59,130,246,0.0)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s ease'
          }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(59,130,246,0.15)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(59,130,246,0)')}
          >
            <i className="pi pi-search-plus" style={{ fontSize: '22px', color: 'white', opacity: 0, transition: 'opacity 0.2s' }} />
          </div>
        </div>
      ) : (
        <div style={{
          height: '100px', display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: bg
        }}>
          <i className={icon} style={{ fontSize: '40px', color }} />
        </div>
      )}

      {/* File info */}
      <div style={{ padding: '10px 12px' }}>
        <div style={{
          fontSize: '12px', fontWeight: 600, color: '#1f2937',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          marginBottom: '4px'
        }}>
          {file.file_name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: 'white',
            background: color, padding: '2px 8px', borderRadius: '6px'
          }}>
            {file.type.toUpperCase()}
          </span>
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            {formatFileSize(file.size)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ViewNoticeModal: React.FC<ViewNoticeModalProps> = ({ open, onClose, noticeId }) => {
  const toast                           = useRef<Toast>(null);
  const [noticeData, setNoticeData]     = useState<NoticeDetails | null>(null);
  const [loading, setLoading]           = useState(true);
  const [isClosing, setIsClosing]       = useState(false);
  const [lightboxSrc, setLightboxSrc]   = useState<string | null>(null);

  useEffect(() => {
    if (!noticeId || !open) {
      console.log('Skipping fetch - noticeId:', noticeId);
      return;
    }

    const fetchNoticeData = async () => {
      setLoading(true);
      setIsClosing(false);
      try {
        console.log('Fetching notice with ID:', noticeId);
        const response = await apiClient.get(`/api/noticeboard?notice_id=${noticeId}`);
        console.log('Notice response:', response.data);
        
        if (response.data.status === 'success') {
          const notice = response.data.notice;
          
          // ✅ Ensure id field exists (fallback to _id)
          if (notice._id && !notice.id) {
            notice.id = notice._id;
          }
          
          // ✅ Ensure files array exists
          if (!notice.files) {
            notice.files = [];
          }
          
          await new Promise(resolve => setTimeout(resolve, 650));
          setNoticeData(notice);
        } else {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: response.data.message || 'Failed to fetch notice', life: 5000 });
          onClose();
        }
      } catch (error: any) {
        console.error('ViewNoticeModal error:', error);
        toast.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || 'Failed to fetch notice', life: 5000 });
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchNoticeData();
  }, [open, noticeId, onClose]);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.p-dialog')?.classList.add('dialog-closing');
      document.querySelector('.p-dialog-mask')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { 
      onClose(); 
      setIsClosing(false); 
      setNoticeData(null);
    }, 400);
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '54px', height: '54px', borderRadius: '14px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 700, flexShrink: 0,
        boxShadow: '0 8px 24px rgba(59,130,246,0.3)', border: '3px solid white'
      }}>
        <i className="pi pi-bell" style={{ fontSize: '22px' }} />
      </div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
          {noticeData?.title || 'Notice Details'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Complete Notice Information</div>
      </div>
    </div>
  );

  const files = noticeData?.files ?? [];

  return (
    <>
      <Toast ref={toast} position="top-right" />

      {lightboxSrc && <ImageLightbox src={lightboxSrc} alt="Preview" onClose={() => setLightboxSrc(null)} />}

      <Dialog
        visible={open && !isClosing} onHide={handleClose} header={headerTemplate}
        style={{ width: '90vw', maxWidth: '800px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px', background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)', borderRadius: '0 0 24px 24px', borderTop: 'none'
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 18px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)', borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)', margin: '-12px -12px 0 -12px'
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn { from { opacity:0; } to { opacity:1; } }
          @keyframes maskFadeOut { from { opacity:1; } to { opacity:0; } }
          .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes dialogOpen { from { opacity:0; transform:scale(0.92) translateY(-20px); } to { opacity:1; transform:scale(1) translateY(0); } }
          @keyframes dialogClose { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(0.88) translateY(25px); } }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241,245,249,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
        `}</style>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: '16px' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading notice details...</span>
          </div>
        ) : noticeData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* ── Notice Details ── */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '16px 18px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <SectionHeader icon="pi pi-file-edit" title="Notice Details" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                  <InfoRow
                    icon="pi pi-flag" label="Priority"
                    value={getPriorityStyle(noticeData.priority).text}
                    isChip chipColor={getPriorityStyle(noticeData.priority).bg}
                  />
                  <InfoRow icon="pi pi-calendar" label="Date & Time" value={formatDateTime(noticeData.date_time)} />
                </div>
                <InfoRow icon="pi pi-user" label="Posted By" value={noticeData.posted_by_name || 'Unknown'} />
                <InfoRow icon="pi pi-users" label="Broadcast To" value={noticeData.broadcast_user_type || 'All'} />

                <div style={{ marginTop: '8px', padding: '16px', borderRadius: '12px', background: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.1)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
                    Notice Title
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e40af', marginBottom: '12px', lineHeight: 1.3 }}>
                    {noticeData.title}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', paddingTop: '12px', borderTop: '1px solid rgba(229,231,235,0.5)' }}>
                    Notice Content
                  </div>
                  <div style={{ fontSize: '14px', color: '#1f2937', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {noticeData.body}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Voice Note ── */}
            {noticeData.voice_note && (
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px 18px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <SectionHeader icon="pi pi-volume-up" title="Voice Note" />
                <VoiceNotePlayer file={noticeData.voice_note} />
              </div>
            )}

            {/* ── Attachments ── */}
            {files.length > 0 && (
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px 18px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <SectionHeader icon="pi pi-paperclip" title={`Attachments (${files.length})`} />

                {/* Images grid */}
                {files.some(f => f.type === 'image') && (
                  <div style={{ marginBottom: files.some(f => f.type !== 'image') ? '16px' : '0' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>
                      Images
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                      {files.filter(f => f.type === 'image').map((file, i) => (
                        <AttachmentCard key={i} file={file} onImageClick={setLightboxSrc} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Non-image files list */}
                {files.some(f => f.type !== 'image') && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>
                      Documents & Files
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {files.filter(f => f.type !== 'image').map((file, i) => {
                        const { icon, color, bg } = getFileIcon(file.type, file.mime_type);
                        return (
                          <div
                            key={i}
                            onClick={() => window.open(file.cdn_url, '_blank')}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '12px',
                              padding: '12px 14px', borderRadius: '10px',
                              border: '1px solid #e5e7eb', background: bg,
                              cursor: 'pointer', transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.borderColor = color;
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.borderColor = '#e5e7eb';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                          >
                            <i className={icon} style={{ fontSize: '24px', color, flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.file_name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                {file.type.toUpperCase()} • {formatFileSize(file.size)}
                              </div>
                            </div>
                            <i className="pi pi-external-link" style={{ fontSize: '14px', color: '#9ca3af', flexShrink: 0 }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default ViewNoticeModal;